import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  IndianRupee, TrendingUp, Receipt, Clock, CheckCircle2,
  XCircle, Loader2, Filter, Building2, User, Percent, ArrowUpRight, ArrowDownRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

interface Transaction {
  id: string;
  service_request_id: string;
  proposal_id: string;
  ca_profile_id: string;
  ca_user_id: string;
  business_user_id: string;
  service_amount: number;
  commission_rate: number;
  commission_amount: number;
  net_amount: number;
  status: string;
  payment_status: string;
  notes: string | null;
  created_at: string;
  service_requests?: { service_type: string; company_name: string } | null;
  ca_profiles?: { name: string; firm_name: string | null } | null;
}

const COMMISSION_RATE = 0.10;

const paymentStatusConfig: Record<string, { label: string; cls: string }> = {
  unpaid: { label: "Unpaid", cls: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20" },
  paid: { label: "Paid", cls: "bg-green-500/10 text-green-600 border-green-500/20" },
  refunded: { label: "Refunded", cls: "bg-destructive/10 text-destructive border-destructive/20" },
};

const statusConfig: Record<string, { label: string; cls: string }> = {
  pending: { label: "Pending", cls: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20" },
  completed: { label: "Completed", cls: "bg-green-500/10 text-green-600 border-green-500/20" },
  cancelled: { label: "Cancelled", cls: "bg-muted text-muted-foreground border-border" },
};

function formatINR(amount: number) {
  return `₹${amount.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

const MarketplaceTransactionsPage = () => {
  const { user, role } = useAuth();
  const { toast } = useToast();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterPayment, setFilterPayment] = useState("all");
  const isCA = role === "ca";

  useEffect(() => {
    if (user) fetchTransactions();
  }, [user]);

  const fetchTransactions = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("marketplace_transactions")
      .select("*, service_requests(service_type, company_name), ca_profiles(name, firm_name)")
      .order("created_at", { ascending: false }) as any;
    if (error) {
      toast({ title: "Failed to load transactions", description: error.message, variant: "destructive" });
    } else {
      setTransactions(data || []);
    }
    setLoading(false);
  };

  const filtered = filterPayment === "all" ? transactions : transactions.filter((t) => t.payment_status === filterPayment);

  const totals = useMemo(() => {
    const total = transactions.reduce((s, t) => s + Number(t.service_amount), 0);
    const commission = transactions.reduce((s, t) => s + Number(t.commission_amount), 0);
    const net = transactions.reduce((s, t) => s + Number(t.net_amount), 0);
    const paid = transactions.filter((t) => t.payment_status === "paid").length;
    return { total, commission, net, paid, count: transactions.length };
  }, [transactions]);

  const markPaid = async (txId: string) => {
    const { error } = await supabase
      .from("marketplace_transactions")
      .update({ payment_status: "paid", updated_at: new Date().toISOString() } as any)
      .eq("id", txId);
    if (error) {
      toast({ title: "Failed to update", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Transaction marked as paid" });
      fetchTransactions();
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground flex items-center gap-2">
            <Receipt className="h-6 w-6 text-accent" />
            Marketplace Transactions
          </h1>
          <p className="text-sm text-muted-foreground">
            {isCA ? "Track your earnings and platform commissions" : "Track service payments and platform fees"}
          </p>
        </div>
        <Select value={filterPayment} onValueChange={setFilterPayment}>
          <SelectTrigger className="w-[130px] h-8 text-xs">
            <Filter className="h-3 w-3 mr-1" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="unpaid">Unpaid</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="refunded">Refunded</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Total Revenue", value: formatINR(totals.total), icon: IndianRupee, sub: `${totals.count} transactions` },
          { label: "Platform Fee (10%)", value: formatINR(totals.commission), icon: Percent, sub: "AccouPro commission" },
          { label: isCA ? "Your Earnings" : "CA Payouts", value: formatINR(totals.net), icon: TrendingUp, sub: "After commission" },
          { label: "Paid", value: `${totals.paid}/${totals.count}`, icon: CheckCircle2, sub: "Settled transactions" },
        ].map((s) => (
          <Card key={s.label} className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center">
                  <s.icon className="h-4 w-4 text-accent" />
                </div>
                <div>
                  <p className="text-lg font-bold text-foreground">{s.value}</p>
                  <p className="text-[10px] text-muted-foreground">{s.label}</p>
                  <p className="text-[9px] text-muted-foreground/70">{s.sub}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Commission Breakdown Info */}
      <Card className="bg-accent/5 border-accent/20">
        <CardContent className="p-3 flex items-center gap-3">
          <Percent className="h-4 w-4 text-accent flex-shrink-0" />
          <div className="text-xs text-foreground">
            <span className="font-semibold">Commission Structure:</span> AccouPro charges a <span className="font-semibold text-accent">10% platform fee</span> on every service transaction.
            For a ₹5,000 service, AccouPro receives ₹500 and the CA receives ₹4,500.
          </div>
        </CardContent>
      </Card>

      {/* Transaction List */}
      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : filtered.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="py-16 text-center">
            <Receipt className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
            <h3 className="text-sm font-semibold text-foreground mb-1">No transactions yet</h3>
            <p className="text-xs text-muted-foreground">Transactions are created when proposals are accepted.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          <AnimatePresence>
            {filtered.map((tx) => {
              const pSts = paymentStatusConfig[tx.payment_status] || paymentStatusConfig.unpaid;
              const tSts = statusConfig[tx.status] || statusConfig.pending;
              return (
                <motion.div key={tx.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                  <Card className="bg-card border-border">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <h3 className="text-sm font-semibold text-foreground">
                              {tx.service_requests?.service_type || "Service"}
                            </h3>
                            <Badge variant="outline" className={`text-[10px] ${pSts.cls}`}>{pSts.label}</Badge>
                            <Badge variant="outline" className={`text-[10px] ${tSts.cls}`}>{tSts.label}</Badge>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
                            <span className="flex items-center gap-1">
                              <Building2 className="h-3 w-3" /> {tx.service_requests?.company_name || "—"}
                            </span>
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" /> {tx.ca_profiles?.name || "CA"}
                              {tx.ca_profiles?.firm_name && ` • ${tx.ca_profiles.firm_name}`}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {new Date(tx.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                            </span>
                          </div>

                          {/* Financial breakdown */}
                          <div className="flex items-center gap-6 p-3 bg-muted/30 rounded-lg border border-border/50">
                            <div className="text-center">
                              <p className="text-[10px] text-muted-foreground mb-0.5">Service Amount</p>
                              <p className="text-sm font-bold text-foreground">{formatINR(Number(tx.service_amount))}</p>
                            </div>
                            <div className="flex items-center text-muted-foreground">
                              <ArrowUpRight className="h-3.5 w-3.5 text-destructive" />
                            </div>
                            <div className="text-center">
                              <p className="text-[10px] text-muted-foreground mb-0.5">Platform Fee ({(Number(tx.commission_rate) * 100).toFixed(0)}%)</p>
                              <p className="text-sm font-semibold text-destructive">{formatINR(Number(tx.commission_amount))}</p>
                            </div>
                            <div className="flex items-center text-muted-foreground">
                              <ArrowDownRight className="h-3.5 w-3.5 text-green-600" />
                            </div>
                            <div className="text-center">
                              <p className="text-[10px] text-muted-foreground mb-0.5">CA Receives</p>
                              <p className="text-sm font-bold text-green-600">{formatINR(Number(tx.net_amount))}</p>
                            </div>
                          </div>
                        </div>

                        {/* Action */}
                        {!isCA && tx.payment_status === "unpaid" && (
                          <Button size="sm" variant="outline" className="gap-1 text-xs ml-3 flex-shrink-0" onClick={() => markPaid(tx.id)}>
                            <CheckCircle2 className="h-3 w-3" /> Mark Paid
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default MarketplaceTransactionsPage;
